import React, { createContext, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  AIClass,
  CubeDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  resetServiceVersion,
  setServiceAndVersion,
} from '../../dataMesh/dataMeshSlice';
import { isAIServiceEnum } from '../../dataMesh/utils/dataMeshUtils';
import {
  RanchPageViewEnum,
  selectRanchPageView,
} from '../../ranches/ranchesSlice';
import {
  CustomerSummaryFilterEnum,
  useFilterCustomerSummary,
} from '../hooks/useFilterCustomerSummary';
import useFindAvailableServicesAndMeasures from '../hooks/useFindAvailableServicesAndMeasures';

interface CurrServicesAndMeasures {
  availableMeasures: CubeMeasureEnum[];
  availableServices: FeatureEnum[];
  availableServicesRaw: AIClass[];
  currService: AIClass | null;
}

export interface CurrServicesAndMeasuresState {
  servicesAndMeasuresWithFeatureFilter: CurrServicesAndMeasures;
  servicesAndMeasuresWithoutFeatureFilter: CurrServicesAndMeasures;
  servicesAndMeasuresWithFeatureFilterAllServiceVersions: CurrServicesAndMeasures;
}

interface ProviderProps {
  children: React.ReactNode;
}

// Create the context
export const CurrServicesAndMeasuresContext = createContext<
  CurrServicesAndMeasuresState | undefined
>(undefined);

// The CurrServicesAndMeasuresContext is designed to provide components with two sets of data related to services and measures: one filtered by feature and one without feature filtering. This dual provision allows components to access either a more restricted or a broader set of data depending on their specific needs in the UI. For example, certain views may require displaying all available data regardless of feature settings (like in a ranch table view or mask toggles), while others need to filter by feature to show only relevant data like when querying scan data for a specific service. The CurrServicesAndMeasuresProvider component uses the useFilterCustomerSummary and useFindAvailableServicesAndMeasures hooks to filter and find the available services and measures based on the customer summary data. It then provides this data to its children through the CurrServicesAndMeasuresContext.Provider component.
export const CurrServicesAndMeasuresProvider: React.FC<ProviderProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const ranchPageView = useAppSelector(selectRanchPageView);

  const filteredCustomerSummaryWithFeatureFilter = useFilterCustomerSummary({
    activeFilters: [
      CustomerSummaryFilterEnum.ranch,
      CustomerSummaryFilterEnum.block,
      CustomerSummaryFilterEnum.scan,
      CustomerSummaryFilterEnum.feature,
      CustomerSummaryFilterEnum.highestAIServiceVersion,
      ...(ranchPageView === RanchPageViewEnum.ranchSummary
        ? [CustomerSummaryFilterEnum.skip]
        : []),
    ],
  });

  const {
    availableServices: availableServicesWithFeatureFilter,
    availableMeasures: availableMeasuresWithFeatureFilter,
    availableServicesRaw: availableServicesRawWithFeatureFilter,
    currService: currServiceWithFeatureFilter,
  } = useFindAvailableServicesAndMeasures(
    filteredCustomerSummaryWithFeatureFilter
  );

  // Used to populate things like image mask toggles where we want to show all available AI services and not just the image masks with the currently selected AI feature in the dropdown.  For example, if "Shoots" is selected in the dropdown, we still want to show image mask toggles for "Buds", "Leaves", etc...
  const filteredCustomerSummaryWithoutFeatureFilter = useFilterCustomerSummary({
    activeFilters: [
      CustomerSummaryFilterEnum.ranch,
      CustomerSummaryFilterEnum.block,
      CustomerSummaryFilterEnum.scan,
      CustomerSummaryFilterEnum.highestAIServiceVersion,
      ...(ranchPageView === RanchPageViewEnum.ranchSummary
        ? [CustomerSummaryFilterEnum.skip]
        : []),
    ],
  });

  const {
    availableServices: availableServicesWithoutFeatureFilter,
    availableMeasures: availableMeasuresWithoutFeatureFilter,
    availableServicesRaw: availableServicesRawWithoutFeatureFilter,
    currService: currServiceWithoutFeatureFilter,
  } = useFindAvailableServicesAndMeasures(
    filteredCustomerSummaryWithoutFeatureFilter
  );

  // Don't filter by version so we can find all available services versions for the selected service and let the user choose which version they want to view
  const filteredCustomerSummaryWithFeatureFilterAllServiceVersions =
    useFilterCustomerSummary({
      activeFilters: [
        CustomerSummaryFilterEnum.ranch,
        CustomerSummaryFilterEnum.block,
        CustomerSummaryFilterEnum.scan,
        CustomerSummaryFilterEnum.feature,
        CustomerSummaryFilterEnum.disableAIServiceVersionUserOverride,
        ...(ranchPageView === RanchPageViewEnum.ranchSummary
          ? [CustomerSummaryFilterEnum.skip]
          : []),
      ],
    });

  const {
    availableServices: availableServicesWithFeatureFilterAllServiceVersions,
    availableMeasures: availableMeasuresWithFeatureFilterAllServiceVersions,
    availableServicesRaw:
      availableServicesRawWithFeatureFilterAllServiceVersions,
    currService: currServiceWithFeatureFilterAllServiceVersions,
  } = useFindAvailableServicesAndMeasures(
    filteredCustomerSummaryWithFeatureFilterAllServiceVersions
  );

  // Update dataMesh state for prototype of data registry / layout
  useEffect(() => {
    if (currServiceWithFeatureFilter) {
      const service = currServiceWithFeatureFilter[CubeDimensionEnum.modelName];
      const version =
        currServiceWithFeatureFilter[CubeDimensionEnum.modelVersion];
      if (service && isAIServiceEnum(service) && version) {
        dispatch(setServiceAndVersion({ service: service, version: version }));
      } else {
        dispatch(resetServiceVersion());
      }
    } else {
      dispatch(resetServiceVersion());
    }
  }, [currServiceWithFeatureFilter, dispatch]);

  const value = {
    servicesAndMeasuresWithFeatureFilter: {
      availableMeasures: availableMeasuresWithFeatureFilter,
      availableServices: availableServicesWithFeatureFilter,
      availableServicesRaw: availableServicesRawWithFeatureFilter,
      currService: currServiceWithFeatureFilter,
    },
    servicesAndMeasuresWithoutFeatureFilter: {
      availableMeasures: availableMeasuresWithoutFeatureFilter,
      availableServices: availableServicesWithoutFeatureFilter,
      availableServicesRaw: availableServicesRawWithoutFeatureFilter,
      currService: currServiceWithoutFeatureFilter,
    },
    servicesAndMeasuresWithFeatureFilterAllServiceVersions: {
      availableMeasures: availableMeasuresWithFeatureFilterAllServiceVersions,
      availableServices: availableServicesWithFeatureFilterAllServiceVersions,
      availableServicesRaw:
        availableServicesRawWithFeatureFilterAllServiceVersions,
      currService: currServiceWithFeatureFilterAllServiceVersions,
    },
  };

  return (
    <CurrServicesAndMeasuresContext.Provider value={value}>
      {children}
    </CurrServicesAndMeasuresContext.Provider>
  );
};
